import React from 'react';
import { ProgressBar } from 'react-bootstrap';
import _ from 'underscore';
import { titleize, humanize, unescapeHTML } from 'underscore.string';
import moment from 'moment';
import accounting from 'accounting';
import { decode } from 'he';
import AppointmentConfirmationBadge from 'components/modules/shipments/stops/AppointmentConfirmationBadge';
import ShipmentListDocuments from 'components/modules/shipments/tracking/ShipmentListDocuments';
import { Translation, useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import i18next from 'i18next';
import { getTimeZoneName } from 'utils/Timezones';
import Badge from '../v2/Badge';
import { getLocationTimezone } from '../../helpers/locations';
import { getPickupStop, getDeliverStop } from '../../helpers/stops';
import { canEditAppointmentWindow } from '../../helpers/shipments';
import { isIntermodalShipmentType } from '../../helpers/shipment-types';
import {
  LoadUnitFormatter,
  LoadDimensionFormatter,
} from '../modules/master/formatters';
import ExternalLink from '../v2/ExternalLink';

export const shipperCostFormatter = (cell, row) => {
  if (!row.receivables.length) {
    return <span>{formatMoney(cell, row?.currency)}</span>;
  }

  // Calculate Drayage Total
  const recTotal = row.receivables.reduce(
    (total, ledgerItem) => total + (ledgerItem?.value || 0),
    0,
  );
  return <span>{formatMoney(recTotal, row?.currency)}</span>;
};

export const carrierPayoutFormatter = (cell, row) => {
  if (!row.payables.length) {
    return <span>{formatMoney(cell, row?.currency)}</span>;
  }

  const payableTotal = row.payables.reduce(
    (total, ledgerItem) => total + (ledgerItem?.value || 0),
    0,
  );
  return <span>{formatMoney(payableTotal, row?.currency)}</span>;
};

export const exceptionReporterFormatter = (cell) => {
  if (cell === 'cargomatic-automation') {
    return (
      <img
        alt="cargomatic-automation"
        src="/img/storm-trooper.jpg"
        className="icon"
        width="25"
      />
    );
  }

  return <span>{cell}</span>;
};

export const currencyFormatter = (cell, row) => (
  <span>{formatMoney(cell, row?.currency)}</span>
);

export const headerFormatter = (column, colIndex, { sortElement }) => {
  const { t } = useTranslation();
  let display_text = column.headerText || column.text;
  if (column.localizationSlug) {
    display_text = titleize(
      t(column.localizationSlug, {
        count: column?.count,
      }),
    );
  }
  if (column.titleize) display_text = titleize(display_text);
  return (
    <div className="text-sm text-grey-darker">
      <strong>{display_text}</strong>
      {sortElement}
    </div>
  );
};

export const masterAccountFormatter = (cell) => {
  if (!cell) return 'NA';
  return cell;
};

const serviceWatchList = ['Service Watch', 'New Customer'];
export const serviceWatchFormatter = (cell) => {
  const text = cell || 'NA';

  let color;
  if (serviceWatchList.includes(cell)) {
    color = 'blue';
  } else {
    color = 'brand';
  }
  return (
    <Translation>
      {(t) => <Badge color={color} text={t(sluggify(text))} />}
    </Translation>
  );
};

export const statusFormatter = (cell) => (
  <Translation>
    {(t) => <Badge color="green" text={titleize(t(sluggify(cell)))} />}
  </Translation>
);

export const accessorialStatusFormatter = (cell) => {
  let color;
  switch (cell) {
    case 'pending':
      color = 'yellow';
      break;
    case 'approved':
      color = 'brand';
      break;
    case 'cancelled':
      color = 'gray';
      break;
    default:
      color = 'purple';
  }

  return <Badge color={color} size="large" rounded />;
};

export function dateTimeFormatter(cell, _row, _rowIndex, extra) {
  return moment(cell).format('L LT');
}

const renderDatetimeRange = (startStr, endStr, timezone) => {
  if (!startStr || !endStr) {
    return i18next.t('NEEDS_APPOINTMENT');
  }

  const dateFormat = 'L';
  const timeFormat = 'LT';

  const dateFormatter = (momentObj) => {
    return momentObj.format(dateFormat);
  };

  const timeFormatter = (momentObj) => {
    return momentObj.format(timeFormat);
  };

  const start = moment(startStr);
  let end;

  if (endStr) {
    end = moment(endStr);
  }

  let startWithTz;
  let endWithTz;

  let formattedTimezone = '';
  if (timezone) {
    startWithTz = start.clone().tz(timezone);
    if (end) {
      endWithTz = end.clone().tz(timezone);
    }
    formattedTimezone = getTimeZoneName(timezone);
  } else {
    startWithTz = start.clone();
    if (end) {
      endWithTz = end.clone();
    }
  }

  const startDate = dateFormatter(startWithTz);
  const startTime = timeFormatter(startWithTz);

  if (end && start.diff(end) === 0) {
    return (
      <div className="nowrap">
        {startDate}
        <br />
        {startTime} {formattedTimezone}
      </div>
    );
  }
  if (end && start.dayOfYear() === end.dayOfYear()) {
    let endTime;
    if (end) {
      endTime = timeFormatter(endWithTz);
    }
    return (
      <div className="nowrap">
        {startDate}
        <br />
        {startTime} <span>-</span> {endTime}{' '}
        <strong style={{ color: '#21B88E' }}>{formattedTimezone}</strong>
      </div>
    );
  }

  let endDate;
  let endTime;

  if (end) {
    endDate = dateFormatter(endWithTz);
    endTime = timeFormatter(endWithTz);
  }
  return (
    <div className="nowrap">
      <div>
        {startDate}
        <br />
        {startTime}
      </div>
      <span>-</span>
      <div>
        {endDate}
        <br />
        {endTime}{' '}
        <strong style={{ color: '#21B88E' }}>{formattedTimezone}</strong>
      </div>
    </div>
  );
};

export const credentialFormatter = (_cell, row) => {
  if (row._type === 'Driver') return row.username;
  return row.email;
};

export const loadDetailsFormatter = (_cell, row) => {
  const rows = [];
  if (row.full_truckload) {
    rows.push(<div key="full-truckload-cell">Truckload</div>);
  }
  _.each(row.loads, (l, i) => {
    rows.push(
      <div key={i}>
        <LoadDimensionFormatter
          quantity={l.quantity}
          type={l.type}
          length={l.length}
          width={l.width}
          height={l.height}
          dimensional_units={l.dimensional_units}
        />
      </div>,
    );
  });

  rows.push(
    <div key="load-weight">
      <LoadUnitFormatter
        weight={accounting.format(row.weight)}
        weight_units={row.weight_units}
      />
    </div>,
  );

  return <div>{rows}</div>;
};

export const datetimeRangeFormatter = (cell, row, rowIndex, extra) => {
  // default to LAX when there's no market
  let timezone = row.market ? row.market.timezone : 'America/Los_Angeles';
  let start = row.pickup_after;
  let end = row.pickup_by;

  let stop;
  let isAdmin = false;
  if (extra) {
    if (extra.admin) {
      isAdmin = true;
    }
    if (extra.type === 'deliver') {
      start = row.deliver_after;
      end = row.deliver_by;
      timezone = getLocationTimezone(row.destination) || timezone;
      stop = getDeliverStop(row);
    } else {
      timezone = getLocationTimezone(row.origin) || timezone;
      stop = getPickupStop(row);
    }
  }

  let canEditWindow = canEditAppointmentWindow(row, isAdmin);

  if (extra.isCarrier && !isIntermodalShipmentType(row)) {
    canEditWindow = false;
  }

  const confirmed = !(stop || {}).appointment_unconfirmed;

  return (
    <div>
      {renderDatetimeRange(start, end, timezone)}
      {row.shipment_type === 'drayage' && (
        <AppointmentConfirmationBadge confirmed={confirmed} hideText />
      )}

      {canEditWindow && extra.onWindowEdit && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => extra.onWindowEdit(extra.type, row)}
          >
            <div className="flex items-center text-sm text-green hover:text-green-darker">
              <i className="mr-1 fa fa-calendar" />

              <Translation>{(t) => <span>{t('EDIT')}</span>}</Translation>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export const defaultFormatter = (cell) => {
  if (!cell) {
    return null;
  }

  return <span>{titleize(humanize(unescapeHTML(cell)))}</span>;
};

export const booleanFormatter = (cell) => {
  return (
    <Translation>
      {(t) => <span>{cell === true ? t('YES') : t('NO')}</span>}
    </Translation>
  );
};

export const dateFormatter = (cell, row, rowIndex, extra) => (
  <span>{cell ? moment(cell).format('L') : ''}</span>
);

export const priceFormatter = (cell) => <span>${cell}</span>;

export const customActionsFormatter = (extraProps) => (cell, row) => {
  if (!extraProps) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center actions">
      {extraProps.canEdit && (
        <a
          href="#"
          className="mr-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            extraProps.onEdit(row);
          }}
        >
          <i className="fa fa-pencil" />
        </a>
      )}
      {extraProps.canDelete && extraProps.onDelete && (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            extraProps.onDelete(row);
          }}
        >
          <i className="fa fa-trash-o" />
        </a>
      )}
    </div>
  );
};

export const entityFormatter = (cell) => (
  <div className="block overflow-hidden text-overflow-ellipsis">
    <a className="">{cell}</a>
  </div>
);

export const equipmentOrderEntityFormatter = (cell) => (
  <div className="block overflow-hidden text-overflow-ellipsis">
    <a className="">{cell}</a>
  </div>
);

export const objectIdTimestampFormatter = (cell, row) => {
  const dateFromObjectId = function (objectId) {
    return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
  };

  return <span>{moment(dateFromObjectId(row._id)).format('YYYY-MM-DD')}</span>;
};

const getColor = (actual, expected) => {
  const diff = Math.abs(actual - expected);

  if (diff >= 30) return 'danger';
  if (diff >= 20) return 'warning';

  return 'success';
};

export const slaFormatter = (cell, row) => {
  const actualColor = getColor(
    row.actual_progress_percentage,
    row.expected_progress_percentage,
  );
  const actualNow =
    row.actual_progress_percentage < 25 ? 25 : row.actual_progress_percentage;
  const expectedColor = getColor(
    row.expected_progress_percentage,
    row.expected_progress_percentage,
  );
  const expectedNow =
    row.expected_progress_percentage < 25
      ? 25
      : row.expected_progress_percentage;
  return (
    <Translation>
      {(t) => (
        <div>
          <span className="font-bold text-grey-darker">{t('ACTUAL')}</span>
          <ProgressBar
            striped
            now={actualNow}
            label={`${row.actual_progress_percentage}%`}
            bsStyle={actualColor}
            style={{ marginBottom: 5 }}
          />
          <span className="font-bold text-grey-darker">{t('EXPECTED')}</span>
          <ProgressBar
            striped
            now={expectedNow}
            label={`${row.expected_progress_percentage}%`}
            bsStyle={expectedColor}
          />
        </div>
      )}
    </Translation>
  );
};

export const rolesFormatter = (cell, row) => {
  if (!cell) return null;
  const roles = _.uniq(row.roles);

  return (
    <div className="md:flex md:flex-wrap md:-mx-1">
      {roles.map((role) => {
        return (
          <div className="mb-2 md:px-1" key={role}>
            <Translation>
              {(t) => <Badge color="blue" text={t(sluggify(role))} />}
            </Translation>
          </div>
        );
      })}
    </div>
  );
};

export const tagsFormatter = (cell, row) => {
  if (!cell) return null;
  const tags = _.uniq(row.tags);

  return (
    <div>
      {tags.map((tag) => {
        return (
          <div className="mb-2">
            <Badge text={tag} />
          </div>
        );
      })}
    </div>
  );
};

export const customerFormatter = (cell, row) => {
  let entityId = row._id;
  let customer;
  let field = row;
  if (cell && typeof cell === 'object') {
    field = cell;
    entityId = cell._id;
  }
  const showPortProIcon =
    field._type === 'Carrier' && 'portpro' in field && field?.portpro?.enabled;

  switch (field._type) {
    case 'Shipper':
      customer = unescapeHTML(`${field.company_name || ''}`);
      break;
    case 'Carrier':
      customer = unescapeHTML(`${field.name || ''}`);
      break;
    default:
      customer = `${field.first_name || ''} ${field.last_name || ''}`;
  }

  return (
    <div className="flex mx-1">
      <a className="" href={`/users/${entityId}`}>
        {titleize(humanize(decode(customer)))}
      </a>
      <div style={{ flexGrow: 0, flexShrink: 0 }} className="mx-1 basis-5">
        {showPortProIcon ? (
          <Translation>
            {(t) => (
              <img
                src="/img/icons/portProLogo.png"
                className="w-full"
                title={t('CARRIER_INTEGRATED_WITH_PORTPRO')}
                alt={t('CARRIER_INTEGRATED_WITH_PORTPRO')}
              />
            )}
          </Translation>
        ) : null}
      </div>
    </div>
  );
};

export const locationFormatter = (cell) => {
  if (!cell) return null;
  const name = cell.name ? cell.name : '';

  return (
    <div>
      <div>
        <label className="mr-2 text-sm font-bold text-grey-darker">
          {humanize(decode(name))}
        </label>
      </div>
      <span className="mb-4">{cell.address}</span>
    </div>
  );
};

export const referenceNumberFormatter = (cell) => {
  const referenceFields = {
    partnerShipmentId: 'Partner Shipment',
    work_order_number: 'Work Order Number',
    house_bill_number: 'House Bill',
    mg_load_id: 'MG Load ID',
    mo_number: 'MO Number',
    tracking_id: 'Tracking ID',
    seal_number: 'Seal #',
    delivery_order: 'Delivery Order',
    master_bol: 'Master Reference',
    pu_number: 'PU Number',
  };

  const uniqueCell = _.uniq(cell || [], function (item) {
    return item.value;
  });

  const referenceNumbers = uniqueCell
    .filter(
      (r) => !r.hidden && Object.keys(referenceFields).indexOf(r.name) > -1,
    )
    .map((refNum, i) => {
      const values = refNum.value?.match(/.{1,20}/g) || []; // Split reference number values into 20 char chunks for display can prob do a max-width?
      return (
        <Translation>
          {(t) => (
            <div key={i}>
              <label className="font-bold text-grey-darker">
                {t(sluggify(referenceFields[refNum.name]))}
              </label>
              <div className="">
                {values.map((value, idx) => (
                  <div key={idx}>
                    <span className="mb-1">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Translation>
      );
    });

  return <div>{referenceNumbers}</div>;
};

const getBadgeData = (_equipmentType) => {
  let badgeColor = 'blue';
  let badgeName = 'container';
  if (_equipmentType === 'chassis_number') {
    badgeColor = 'purple';
    badgeName = 'chassis';
  }
  if (_equipmentType === 'trailer_number') {
    badgeColor = 'grey';
    badgeName = 'trailer';
  }

  return { badgeColor, badgeName };
};

export const equipmentListFormatter = (cell) => {
  const referenceFields = {
    chassis_number: 'Chassis Number',
    equipment_id: 'Equipment ID',
    trailer_number: 'Trailer Number',
  };

  const uniqueCell = _.uniq(cell || [], function (item) {
    return item.value;
  });

  const referenceNumbers = uniqueCell
    .filter(
      (r) => !r.hidden && Object.keys(referenceFields).indexOf(r.name) > -1,
    )
    .map((refNum, i) => {
      const values = refNum.value?.match(/.{1,20}/g) || []; // Split reference number values into 20 char chunks for display can prob do a max-width?
      const badgeData = getBadgeData(refNum.name);
      return (
        <Translation>
          {(t) => (
            <div
              key={refNum.value}
              className="flex items-center mb-2 space-x-1"
            >
              {`${refNum.value}`}
              <Badge color={badgeData.badgeColor}>
                {t(sluggify(badgeData.badgeName))}
              </Badge>
            </div>
          )}
        </Translation>
      );
    });

  return <div>{referenceNumbers}</div>;
};

export const InvoiceFormatter = (cell) => {
  let invoiceSent = 'N/A';
  if (cell.length) {
    invoiceSent = 'No';
    const invoiceFlags = cell.map((invoice) => {
      return invoice.email_sent;
    });
    if (invoiceFlags.every((flag) => flag === true)) {
      invoiceSent = 'Yes';
    }
  }

  return <div>{invoiceSent}</div>;
};

export const createResourceFormatter =
  (resource) => (cell, row, rowIndex, extra) => {
    /* when a column displays a sub entity, you can specify
     * the sub entities display attribute in the column mappings.
     * example src/main/js/components/modules/equipment/Mapping.js ->
     * formatExtraData: {
     *   attribute: 'order_id',
     * },
     */
    const subEntityField = extra && extra.attribute ? extra.attribute : null;
    let entityId = typeof cell === 'object' ? (cell || {})._id : row._id;
    let isCreditMemo =
      row.credit_memos &&
      row.credit_memos.length &&
      cell === row.credit_memos[0].number;
    if (resource === 'invoices' && isCreditMemo) {
      entityId = row.credit_memos[0]._id;
    }
    if (extra && extra.entityIdField) {
      entityId = row[extra.entityIdField];
    }

    // Need either entityID to construct the link or extra.onClick for custom handling.
    if (!(entityId || (extra && extra.onClick))) {
      return null;
    }
    const field = cell && subEntityField ? cell[subEntityField] : cell;
    const link = `/${resource}/${entityId}`;

    if (extra && extra.onClick) {
      return (
        <div className="block overflow-hidden text-overflow-ellipsis">
          <a
            className=""
            title={field}
            href="#"
            onClick={(e) => extra.onClick(row, e)}
          >
            {field}
          </a>
        </div>
      );
    }
    if (extra && extra.linkTarget) {
      return (
        <div className="block overflow-hidden text-overflow-ellipsis">
          <a
            className=""
            title={field}
            href={link}
            target={extra.linkTarget}
            rel="noreferrer noopener"
          >
            {field}
          </a>
        </div>
      );
    }
    return (
      <div className="block overflow-hidden text-overflow-ellipsis">
        <ExternalLink title={field} href={link} showIcon={!!field} />
      </div>
    );
  };

export const genericFormatter = (cell) => <span>{cell}</span>;

export const documentsFormatter = (cell, row) => {
  return <ShipmentListDocuments documents={cell} shipment={row} />;
};

export const getEntityFormatter = (type) => {
  switch (type) {
    case 'location':
      return createResourceFormatter('locations');
    case 'order':
      return createResourceFormatter('orders');
    case 'shipment':
      return createResourceFormatter('shipments');
    case 'customer':
      return createResourceFormatter('users');
    case 'invoice':
      return createResourceFormatter('invoices');
    case 'payout':
      return createResourceFormatter('payouts');
    default:
      return entityFormatter;
  }
};
